import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Plus, Trash2, Save, Loader2, AlertCircle, X
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand',
  'Karnataka', 'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur',
  'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab',
  'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura',
  'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Delhi', 'Jammu and Kashmir', 'Ladakh',
  'Andaman and Nicobar Islands', 'Chandigarh', 'Dadra and Nagar Haveli and Daman and Diu',
  'Lakshadweep', 'Puducherry'
];

const emptyLineItem = () => ({
  key: Date.now() + Math.random(),
  product_id: '',
  serial_number: '',
  description: '',
  mrp: '',
  quantity: 1,
  unit_id: '',
  price_per_unit: '',
  discount_percentage: 0,
  discount_amount: 0,
  tax_id: '',
  tax_amount: 0,
  total_amount: 0
});

export const ProformaInvoiceCreate = () => {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const [customers, setCustomers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [products, setProducts] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [units, setUnits] = useState([]);

  const [customerId, setCustomerId] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [refNumber, setRefNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().split('T')[0]);
  const [stateOfSupply, setStateOfSupply] = useState('');

  const [lineItems, setLineItems] = useState([emptyLineItem()]);
  const [description, setDescription] = useState('');
  const [roundOff, setRoundOff] = useState(true);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  useEffect(() => {
    const fetchMasterData = async () => {
      try {
        const [customersRes, companiesRes, productsRes, taxesRes, unitsRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/customers`, getAuthHeaders()),
          axios.get(`${BACKEND_URL}/api/companies`, getAuthHeaders()),
          axios.get(`${BACKEND_URL}/api/products`, getAuthHeaders()),
          axios.get(`${BACKEND_URL}/api/taxes`, getAuthHeaders()),
          axios.get(`${BACKEND_URL}/api/units`, getAuthHeaders())
        ]);
        setCustomers(customersRes.data.filter(c => c.active));
        setCompanies(companiesRes.data.filter(c => c.active));
        setProducts(productsRes.data);
        setTaxes(taxesRes.data.filter(t => t.active));
        setUnits(unitsRes.data.filter(u => u.active));
      } catch (err) {
        setError('Failed to load master data');
      }
    };
    fetchMasterData();
  }, []);

  const recalcLine = (item) => {
    const qty = parseFloat(item.quantity) || 0;
    const price = parseFloat(item.price_per_unit) || 0;
    const discPct = parseFloat(item.discount_percentage) || 0;
    const lineTotal = qty * price;
    const discAmt = lineTotal * discPct / 100;
    const taxableAmount = lineTotal - discAmt;
    const tax = taxes.find(t => t.id === item.tax_id);
    const taxPct = tax ? parseFloat(tax.percentage) : 0;
    const taxAmt = taxableAmount * taxPct / 100;
    const totalAmt = taxableAmount + taxAmt;
    return {
      ...item,
      discount_amount: Math.round(discAmt * 100) / 100,
      tax_amount: Math.round(taxAmt * 100) / 100,
      total_amount: Math.round(totalAmt * 100) / 100
    };
  };

  const updateLineItem = (index, field, value) => {
    setLineItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      if (field === 'product_id') {
        const prod = products.find(p => p.id === value);
        if (prod && prod.unit_id) updated[index].unit_id = prod.unit_id;
        if (value && !updated[updated.length - 1]?.product_id) return updated.map(i => recalcLine(i));
        if (value) updated.push(emptyLineItem());
      }
      updated[index] = recalcLine(updated[index]);
      return updated;
    });
  };

  const addLineItem = () => setLineItems(prev => [...prev, emptyLineItem()]);
  const removeLineItem = (index) => {
    if (lineItems.length <= 1) return;
    setLineItems(prev => prev.filter((_, i) => i !== index));
  };

  const subtotal = lineItems.reduce((sum, item) => sum + (item.total_amount || 0), 0);
  const roundOffAmount = roundOff ? Math.round(subtotal) - subtotal : 0;
  const total = roundOff ? Math.round(subtotal) : Math.round(subtotal * 100) / 100;
  const totalQty = lineItems.reduce((sum, item) => sum + (parseFloat(item.quantity) || 0), 0);
  const totalDiscount = lineItems.reduce((sum, item) => sum + (item.discount_amount || 0), 0);
  const totalTax = lineItems.reduce((sum, item) => sum + (item.tax_amount || 0), 0);

  const handleSave = async () => {
    setError('');
    if (!customerId) { setError('Please select a customer (party)'); return; }
    if (!lineItems.some(li => li.product_id)) { setError('Please add at least one product'); return; }

    setSubmitting(true);
    try {
      const payload = {
        customer_id: customerId,
        company_id: companyId || null,
        ref_number: refNumber,
        invoice_date: invoiceDate,
        state_of_supply: stateOfSupply,
        line_items: lineItems.filter(li => li.product_id).map(li => ({
          product_id: li.product_id,
          description: li.description,
          mrp: parseFloat(li.mrp) || null,
          quantity: parseFloat(li.quantity) || 0,
          unit_id: li.unit_id || null,
          price_per_unit: parseFloat(li.price_per_unit) || 0,
          discount_percentage: parseFloat(li.discount_percentage) || 0,
          discount_amount: li.discount_amount,
          tax_id: li.tax_id || null,
          tax_amount: li.tax_amount,
          total_amount: li.total_amount
        })),
        description: description || null,
        round_off: roundOff,
        round_off_amount: Math.round(roundOffAmount * 100) / 100,
        subtotal: Math.round(subtotal * 100) / 100,
        total
      };
      await axios.post(`${BACKEND_URL}/api/proforma-invoices`, payload, getAuthHeaders());
      navigate('/admin/sale/proforma');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save proforma invoice');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="proforma-create-page" className="pb-24">
      <button onClick={() => navigate('/admin/sale/proforma')} data-testid="back-btn"
        className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6 transition-colors">
        <ArrowLeft className="h-4 w-4" /> Back to Proforma Invoices
      </button>

      <h1 className="text-3xl font-bold text-slate-900 uppercase tracking-tight mb-8">Proforma Invoice</h1>

      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-6">
          <AlertCircle className="h-5 w-5" /><span>{error}</span>
          <button onClick={() => setError('')} className="ml-auto"><X className="h-4 w-4" /></button>
        </div>
      )}

      {/* HEADER */}
      <div className="bg-white rounded-sm border border-slate-200 p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Party (Customer) <span className="text-red-500">*</span></label>
            <select value={customerId} onChange={(e) => setCustomerId(e.target.value)} data-testid="select-customer"
              className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none">
              <option value="">Select Customer</option>
              {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Company</label>
            <select value={companyId} onChange={(e) => setCompanyId(e.target.value)} data-testid="select-company"
              className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none">
              <option value="">Select Company</option>
              {companies.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Quotation Number</label>
            <input type="text" value={refNumber} onChange={(e) => setRefNumber(e.target.value)} placeholder="e.g., PI/25-26/8"
              data-testid="input-ref-number" className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Invoice Date</label>
            <input type="date" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)}
              data-testid="input-date" className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">State of Supply</label>
            <select value={stateOfSupply} onChange={(e) => setStateOfSupply(e.target.value)} data-testid="select-state"
              className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none">
              <option value="">Select State</option>
              {STATES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* LINE ITEMS */}
      <div className="bg-white rounded-sm border border-slate-200 mb-6">
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
          <h2 className="text-lg font-bold text-slate-900 uppercase tracking-tight">Line Items</h2>
          <button onClick={addLineItem} data-testid="add-line-item-btn"
            className="inline-flex items-center gap-1 text-orange-600 hover:text-orange-700 font-medium text-sm">
            <Plus className="h-4 w-4" /> Add Row
          </button>
        </div>
        <div className="overflow-x-auto">
          <div className="min-w-[1200px]">
            <div className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-slate-200 bg-slate-50 text-xs font-semibold text-slate-600 uppercase tracking-wider">
              <div className="col-span-1">#</div>
              <div className="col-span-2">Product</div>
              <div className="col-span-1">MRP</div>
              <div className="col-span-1">Qty</div>
              <div className="col-span-1">Unit</div>
              <div className="col-span-1">Price/Unit</div>
              <div className="col-span-1">Disc %</div>
              <div className="col-span-1">Disc Amt</div>
              <div className="col-span-1">Tax</div>
              <div className="col-span-1">Tax Amt</div>
              <div className="col-span-1">Amount</div>
            </div>
            {lineItems.map((item, idx) => (
              <div key={item.key} data-testid={`line-item-${idx}`}
                className="grid grid-cols-12 gap-2 px-4 py-2 border-b border-slate-100 items-center">
                <div className="col-span-1 flex items-center gap-1">
                  <span className="text-slate-500 text-sm">{idx + 1}</span>
                  {lineItems.length > 1 && (
                    <button onClick={() => removeLineItem(idx)} data-testid={`remove-line-${idx}`}
                      className="p-1 text-slate-400 hover:text-red-500 transition-colors"><Trash2 className="h-3 w-3" /></button>
                  )}
                </div>
                <div className="col-span-2">
                  <select value={item.product_id} onChange={(e) => updateLineItem(idx, 'product_id', e.target.value)}
                    data-testid={`line-product-${idx}`}
                    className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none">
                    <option value="">Select</option>
                    {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                  <input type="text" value={item.serial_number} onChange={(e) => updateLineItem(idx, 'serial_number', e.target.value)}
                    placeholder="Serial Number" className="w-full px-2 py-1 border border-slate-100 rounded-sm text-xs mt-1 focus:border-orange-500 focus:outline-none" />
                  <input type="text" value={item.description} onChange={(e) => updateLineItem(idx, 'description', e.target.value)}
                    placeholder="Description" className="w-full px-2 py-1 border border-slate-100 rounded-sm text-xs mt-1 focus:border-orange-500 focus:outline-none" />
                </div>
                <div className="col-span-1">
                  <input type="number" value={item.mrp} onChange={(e) => updateLineItem(idx, 'mrp', e.target.value)}
                    placeholder="0" className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none" />
                </div>
                <div className="col-span-1">
                  <input type="number" value={item.quantity} onChange={(e) => updateLineItem(idx, 'quantity', e.target.value)}
                    min="0" data-testid={`line-qty-${idx}`}
                    className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none" />
                </div>
                <div className="col-span-1">
                  <select value={item.unit_id} onChange={(e) => updateLineItem(idx, 'unit_id', e.target.value)}
                    className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none">
                    <option value="">-</option>
                    {units.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                </div>
                <div className="col-span-1">
                  <input type="number" value={item.price_per_unit} onChange={(e) => updateLineItem(idx, 'price_per_unit', e.target.value)}
                    placeholder="0" step="0.01" data-testid={`line-price-${idx}`}
                    className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none" />
                </div>
                <div className="col-span-1">
                  <input type="number" value={item.discount_percentage} onChange={(e) => updateLineItem(idx, 'discount_percentage', e.target.value)}
                    min="0" max="100" step="0.01"
                    className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none" />
                </div>
                <div className="col-span-1"><span className="text-sm text-slate-600 px-2">{item.discount_amount?.toFixed(2)}</span></div>
                <div className="col-span-1">
                  <select value={item.tax_id} onChange={(e) => updateLineItem(idx, 'tax_id', e.target.value)}
                    data-testid={`line-tax-${idx}`}
                    className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none">
                    <option value="">-</option>
                    {taxes.map(t => <option key={t.id} value={t.id}>{t.name} ({t.percentage}%)</option>)}
                  </select>
                </div>
                <div className="col-span-1"><span className="text-sm text-slate-600 px-2">{item.tax_amount?.toFixed(2)}</span></div>
                <div className="col-span-1"><span className="text-sm font-medium text-slate-900 px-2">{item.total_amount?.toFixed(2)}</span></div>
              </div>
            ))}
            <div className="grid grid-cols-12 gap-2 px-4 py-3 bg-slate-50 border-t border-slate-200 text-sm font-semibold">
              <div className="col-span-1"></div>
              <div className="col-span-2 text-slate-700">TOTAL</div>
              <div className="col-span-1"></div>
              <div className="col-span-1 text-slate-700">{totalQty}</div>
              <div className="col-span-1"></div>
              <div className="col-span-1"></div>
              <div className="col-span-1"></div>
              <div className="col-span-1 text-slate-700">{totalDiscount.toFixed(2)}</div>
              <div className="col-span-1"></div>
              <div className="col-span-1 text-slate-700">{totalTax.toFixed(2)}</div>
              <div className="col-span-1 text-slate-900">{subtotal.toFixed(2)}</div>
            </div>
          </div>
        </div>
      </div>

      {/* BOTTOM — Description + Round Off + Total */}
      <div className="bg-white rounded-sm border border-slate-200 p-6 mb-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Add Description</label>
              <textarea value={description} onChange={(e) => setDescription(e.target.value)}
                rows={2} placeholder="Add a note or description..."
                data-testid="input-description"
                className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none text-sm resize-none" />
            </div>
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <input type="checkbox" checked={roundOff} onChange={(e) => setRoundOff(e.target.checked)}
                  data-testid="round-off-checkbox" className="w-4 h-4 text-orange-500 border-slate-300 rounded" />
                <span className="text-sm text-slate-600">Round Off</span>
                {roundOff && <span className="text-sm text-slate-500">{roundOffAmount >= 0 ? '+' : ''}{roundOffAmount.toFixed(2)}</span>}
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-slate-700">Total</span>
                <span className="text-lg font-bold text-slate-900 min-w-[100px] text-right" data-testid="total-amount">
                  ₹{total.toLocaleString()}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* SAVE */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 px-6 py-4 flex items-center justify-end z-40">
        <button onClick={handleSave} disabled={submitting} data-testid="save-btn"
          className="inline-flex items-center gap-2 bg-orange-500 text-white font-bold tracking-wider uppercase px-8 py-2.5 rounded-sm hover:bg-orange-600 transition-colors disabled:opacity-50">
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save
        </button>
      </div>
    </div>
  );
};
